import React, { useCallback, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Chip,
  FormControl,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  PersonAdd as PersonAddIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Notifications,
  ExitToApp,
  LocalShipping,
  LocalOffer as CouponIcon,
  Approval as ApprovalIcon,
  Campaign as CampaignIcon,
  Place as PlaceIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  LocationCity as LocationCityIcon,
  History as HistoryIcon,
  PublicOff as ImpersonateIcon,
  Storefront as AgencyIcon,
  EventRepeat as SubscriptionIcon,
  Payments as WithdrawalIcon,
} from '@mui/icons-material';
import adminService, { City } from '../../services/adminService';
import { useAdmin } from '../../contexts/AdminContext';
import { useColorMode } from '../../contexts/ColorModeContext';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationPanel from '../../contexts/NotificationPanel';
import NotificationPermissionBanner from '../NotificationPermissionBanner';
import { OfflineBanner } from '../common/OfflineBanner';
import { alpha } from '@mui/material';
import { brand, tokens } from '../../theme';

/**
 * The one layout shell. Every page renders into it and none re-declare chrome.
 *
 * Three things it owns that pages must never reimplement:
 *
 *  · Navigation, grouped into sections. Twelve flat items is a list to read;
 *    grouped, it is a structure to scan.
 *  · The responsive rule. Below `tokens.mobileBreakpoint` the rail becomes a
 *    temporary drawer. The same token drives ResponsiveTable, so the sidebar
 *    and the tables can never disagree about what "mobile" means.
 *  · The desktop collapse, which trades labels for screen width on the
 *    table-heavy screens. Remembered per browser, because someone who wants
 *    the room wants it on every page, not once.
 */

const COLLAPSE_KEY = 'pn.admin.sidebarCollapsed';

interface NavigationItem {
  text: string;
  path: string;
  icon: React.ReactElement;
  badge?: number;
}

interface NavigationSection {
  /** Shown above the group when expanded; a divider when collapsed. */
  label: string;
  items: NavigationItem[];
}

const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(tokens.mobileBreakpoint));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      // Private browsing, or storage disabled. Expanded is the safe default.
      return false;
    }
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, logout, dashboardStats, isSuperAdmin, impersonatingCityId, startImpersonating, stopImpersonating } =
    useAdmin();
  const [impersonationCities, setImpersonationCities] = useState<City[]>([]);

  React.useEffect(() => {
    if (!isSuperAdmin) return;
    adminService.getAllCities().then((cities) => setImpersonationCities(cities.filter((c) => c.isActive)));
  }, [isSuperAdmin]);
  const { unreadCount } = useNotifications();
  const { mode, toggleColorMode } = useColorMode();

  React.useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        /* preference simply is not remembered */
      }
      return next;
    });
  }, []);

  const sections: NavigationSection[] = useMemo(
    () => [
      {
        label: 'Operations',
        items: [
          { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
          {
            text: 'Tasks',
            path: '/tasks',
            icon: <AssignmentIcon />,
            badge: dashboardStats.activeTasks || undefined,
          },
          {
            text: 'Ninjas',
            path: '/ninjas',
            icon: <PersonAddIcon />,
            badge: dashboardStats.pendingNinjas || undefined,
          },
          { text: 'Agencies', path: '/agencies', icon: <AgencyIcon /> },
          { text: 'Subscriptions', path: '/subscriptions', icon: <SubscriptionIcon /> },
          { text: 'Withdrawals', path: '/withdrawals', icon: <WithdrawalIcon /> },
          { text: 'Customers', path: '/customers', icon: <PeopleIcon /> },
          {
            text: 'Approvals',
            path: '/ninja-preferences',
            icon: <ApprovalIcon />,
          },
        ],
      },
      {
        label: 'Catalogue',
        items: [
          { text: 'Services & Pricing', path: '/services', icon: <LocalShipping /> },
          { text: 'Coupons', path: '/coupons', icon: <CouponIcon /> },
          { text: 'Banners & Ads', path: '/banner-ads', icon: <CampaignIcon /> },
          { text: 'Areas', path: '/areas', icon: <PlaceIcon /> },
        ],
      },
      {
        label: 'Insights',
        items: [
          { text: 'Analytics', path: '/analytics', icon: <AnalyticsIcon /> },
          { text: 'Area Statistics', path: '/area-statistics', icon: <AnalyticsIcon /> },
        ],
      },
      {
        label: 'System',
        items: [{ text: 'Settings', path: '/settings', icon: <SettingsIcon /> }],
      },
      ...(isSuperAdmin
        ? [
            {
              label: 'Super Admin',
              items: [
                { text: 'Cities', path: '/super-admin/cities', icon: <LocationCityIcon /> },
                { text: 'Admin Users', path: '/super-admin/admins', icon: <PeopleIcon /> },
                { text: 'Audit Log', path: '/super-admin/audit-log', icon: <HistoryIcon /> },
              ],
            },
          ]
        : []),
    ],
    [dashboardStats.activeTasks, dashboardStats.pendingNinjas, isSuperAdmin],
  );

  const allItems = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const currentTitle =
    allItems.find((item) => location.pathname.startsWith(item.path))?.text ??
    'Admin Panel';

  const railWidth = collapsed && !isMobile ? tokens.sidebar.collapsedWidth : tokens.sidebar.width;

  const go = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  // ── The rail ──────────────────────────────────────────────────────────────
  const rail = (
    <Stack
      sx={{
        height: '100%',
        color: 'rgba(255,255,255,0.72)',
        overflowX: 'hidden',
        position: 'relative',
        // The rail is always dark regardless of app mode — a fixed depth cue
        // like most admin shells — so its richness comes from a self-
        // contained glow rather than the page's own ambient background,
        // which would have nothing to blend with here (nothing renders
        // behind a permanent drawer for backdrop-filter to pick up).
        background: [
          `radial-gradient(360px circle at 20% 0%, ${alpha(brand.orange, 0.16)}, transparent 60%)`,
          `linear-gradient(180deg, ${brand.ink} 0%, ${brand.inkLight} 100%)`,
        ].join(', '),
        borderRight: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {/* Brand */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ px: collapsed && !isMobile ? 1.5 : 2, py: 2.25, minHeight: tokens.topBarHeight }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            flexShrink: 0,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: brand.orange,
            color: '#FFFFFF',
            fontWeight: 800,
            fontSize: 15,
          }}
        >
          PN
        </Box>
        {!(collapsed && !isMobile) && (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }} noWrap>
              PickupNinja
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }} noWrap>
              Admin
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <IconButton
            size="small"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
          >
            {collapsed ? (
              <KeyboardDoubleArrowRight fontSize="small" />
            ) : (
              <KeyboardDoubleArrowLeft fontSize="small" />
            )}
          </IconButton>
        )}
      </Stack>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: 1.25, pb: 1 }}>
        {sections.map((section, index) => (
          <Box key={section.label} sx={{ mb: 1 }}>
            {collapsed && !isMobile ? (
              index > 0 && (
                <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.08)' }} />
              )
            ) : (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 1.5,
                  pt: 1.5,
                  pb: 0.75,
                  color: 'rgba(255,255,255,0.38)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontWeight: 700,
                  fontSize: '0.6875rem',
                }}
              >
                {section.label}
              </Typography>
            )}
            <List disablePadding>
              {section.items.map((item) => {
                const selected = location.pathname.startsWith(item.path);
                const button = (
                  <ListItemButton
                    selected={selected}
                    onClick={() => go(item.path)}
                    sx={{
                      minHeight: 44,
                      borderRadius: `${tokens.radius.control}px`,
                      mb: 0.25,
                      px: 1.5,
                      justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                      color: selected ? '#FFFFFF' : 'rgba(255,255,255,0.72)',
                      '&.Mui-selected': {
                        bgcolor: brand.orange,
                        '&:hover': { bgcolor: brand.orangeLight },
                        '& .MuiListItemIcon-root': { color: '#FFFFFF' },
                      },
                      '&:hover': {
                        bgcolor: selected ? brand.orangeLight : 'rgba(255,255,255,0.06)',
                        color: selected ? '#fff' : '#fff',
                        '& .MuiListItemIcon-root': {
                          color: '#fff',
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: collapsed && !isMobile ? 0 : 36,
                        color: selected ? '#FFFFFF' : 'rgba(255,255,255,0.6)',
                      }}
                    >
                      {item.badge ? (
                        <Badge badgeContent={item.badge} color="error" max={99}>
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    {!(collapsed && !isMobile) && (
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: selected ? 700 : 500,
                          noWrap: true,
                        }}
                      />
                    )}
                  </ListItemButton>
                );

                return (
                  <ListItem key={item.path} disablePadding>
                    {collapsed && !isMobile ? (
                      <Tooltip title={item.text} placement="right">
                        <Box sx={{ width: '100%' }}>{button}</Box>
                      </Tooltip>
                    ) : (
                      button
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Signed-in user, pinned to the bottom */}
      <Box sx={{ p: 1.25, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{
            px: collapsed && !isMobile ? 0 : 1,
            py: 1,
            borderRadius: `${tokens.radius.control}px`,
            justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
          }}
        >
          <Avatar sx={{ width: 34, height: 34, bgcolor: brand.orange, color: '#FFFFFF', fontWeight: 700, fontSize: 14 }}>
            {(user?.email ?? '?').charAt(0).toUpperCase()}
          </Avatar>
          {!(collapsed && !isMobile) && (
            <>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }} noWrap>
                  {user?.email?.split('@')[0] ?? 'Admin'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }} noWrap>
                  Administrator
                </Typography>
              </Box>
              <Tooltip title="Sign out">
                <IconButton
                  size="small"
                  onClick={async () => {
                    await logout();
                    navigate('/login');
                  }}
                  aria-label="Sign out"
                  sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
                >
                  <ExitToApp fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Box>
    </Stack>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Typography color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  if (!user) return null;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Rail */}
      <Box
        component="nav"
        sx={{
          width: { xs: 0, [tokens.mobileBreakpoint]: railWidth },
          flexShrink: 0,
          transition: 'width 180ms ease',
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', [tokens.mobileBreakpoint]: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: tokens.sidebar.width,
              border: 0,
            },
          }}
        >
          {rail}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', [tokens.mobileBreakpoint]: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: railWidth,
              border: 0,
              transition: 'width 180ms ease',
              overflowX: 'hidden',
            },
          }}
        >
          {rail}
        </Drawer>
      </Box>

      {/* Content column */}
      <Box
        sx={{
          flexGrow: 1,
          // Without this a flex item refuses to shrink below its content,
          // which is what lets a wide table drag the whole page sideways.
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top bar */}
        <Stack
          component="header"
          direction="row"
          alignItems="center"
          spacing={1}
          sx={(t) => ({
            position: 'sticky',
            top: 0,
            zIndex: t.zIndex.appBar,
            minHeight: tokens.topBarHeight,
            px: { xs: 1.5, md: 3 },
            borderBottom: 1,
            borderColor: 'divider',
            // Frosted rather than flat, so content that scrolls up under it
            // stays legible (title, chips) without a hard clip line — a
            // standard "glass header" treatment for a page that already has
            // real depth (the ambient glow, cards) for it to pick up.
            ...(t.palette.mode === 'dark'
              ? {
                  bgcolor: alpha(t.palette.background.default, 0.72),
                  backdropFilter: 'blur(16px) saturate(140%)',
                  WebkitBackdropFilter: 'blur(16px) saturate(140%)',
                }
              : { bgcolor: 'background.default' }),
          })}
        >
          <IconButton
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            sx={{ display: { xs: 'inline-flex', [tokens.mobileBreakpoint]: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
            {currentTitle}
          </Typography>

          {isSuperAdmin && impersonationCities.length > 0 && (
            impersonatingCityId ? (
              <Chip
                icon={<ImpersonateIcon />}
                color="warning"
                variant="filled"
                label={`Viewing as ${impersonationCities.find((c) => c.id === impersonatingCityId)?.name || 'city'}`}
                onDelete={stopImpersonating}
                deleteIcon={<Tooltip title="Return to super admin view"><span>✕</span></Tooltip>}
                sx={{ fontWeight: 600 }}
              />
            ) : (
              <FormControl size="small" sx={{ minWidth: 160, display: { xs: 'none', sm: 'block' } }}>
                <Select
                  displayEmpty
                  value=""
                  onChange={(e) => {
                    const cityId = e.target.value as string;
                    if (cityId) startImpersonating(cityId);
                  }}
                  renderValue={() => 'View as city…'}
                  sx={{ '& .MuiSelect-select': { py: 0.75 } }}
                >
                  {impersonationCities.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )
          )}

          <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
            <IconButton onClick={toggleColorMode} aria-label="Toggle colour mode">
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Notifications">
            <IconButton
              onClick={() => setNotificationPanelOpen(true)}
              aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
            >
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-label="Account menu"
            aria-haspopup="true"
          >
            <AccountCircle />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
          >
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate('/profile');
              }}
            >
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2" noWrap>
                  {user.email}
                </Typography>
              </ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={async () => {
                setAnchorEl(null);
                await logout();
                navigate('/login');
              }}
            >
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sign out</ListItemText>
            </MenuItem>
          </Menu>
        </Stack>

        {/* Page body */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            maxWidth: '100%',
            overflowX: 'hidden',
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 2, md: 3 },
          }}
        >
          <OfflineBanner />
          <NotificationPermissionBanner />
          <Outlet />
        </Box>
      </Box>

      <NotificationPanel
        open={notificationPanelOpen}
        onClose={() => setNotificationPanelOpen(false)}
      />
    </Box>
  );
};

export default Layout;
